import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  forwardRef,
  Input,
  QueryList,
} from '@angular/core';
import { InputBaseComponent, withDefaultFalse, withDefaultString } from '@backbase/ui-ang/base-classes';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  BB_DROPDOWN_CHANGE_DETECTION_REF_TOKEN,
  DropdownSingleSelectOptionComponent,
} from './dropdown-single-select-option.component';
import { withDefaultCompareWithFn, withDefaultIconName, withDefaultOptions } from './dropdown-single-select.transforms';

/**
 * Generic object interface for dropdown option properties.
 *
 * @remarks
 * Used internally for object property path resolution when options are provided as objects
 * rather than strings. Allows flexible property access for displaying option text.
 *
 * @example
 *
 * ```typescript
 * const option: Option = {
 *   id: 1,
 *   name: 'Option One',
 *   description: 'First option'
 * };
 * ```
 */
interface Option {
  [key: string]: any;
}

/**
 * Interface defining the input properties for {@link DropdownSingleSelectComponent}.
 *
 * @remarks
 * This interface extends the component's configurable properties and is commonly used
 * in form libraries and type-safe component configuration. It defines all the input
 * properties that can be bound to the dropdown component.
 *
 * @example
 *
 * ```typescript
 * const dropdownConfig: DropdownSingleSelectComponentProps = {
 *   placeholder: 'Select an option',
 *   defaultOptionAsPlaceholder: false,
 *   options: ['Option 1', 'Option 2', 'Option 3']
 * };
 * ```
 */
export interface DropdownSingleSelectComponentProps {
  /**
   * The placeholder for the select. Defaults to an empty string;
   *
   * @example
   *
   * ```html
   * <bb-dropdown-single-select-ui
   *   [placeholder]="'Select an option'"
   *   [formControl]="formControl"
   * />
   * ```
   */
  placeholder: string;

  /**
   * This Boolean attribute determine if default option as placeholder is displayed. Defaults to false.
   *
   * @example
   *
   * ```html
   * <bb-dropdown-single-select-ui
   *   [defaultOptionAsPlaceholder]="true"
   *   [formControl]="formControl"
   * />
   * ```
   */
  defaultOptionAsPlaceholder: boolean;

  /**
   * The value for the input field. It can be a list of strings or list of objects.
   * If the value is a list of objects, you have to provide the `displayAttributePath` property.
   */
  options: Array<Object | string>;

  /**
   * In case the list of options is a list of objects,
   * this property will set the path to the object property which value will be used to display the option.
   * Undefined will assume the options are strings.
   * Child Nodes should be seperated by '.'
   */
  displayAttributePath?: string;

  /**
   * Right corner icon name.
   * Default name 'toggle-down'
   */
  iconName: string;

  /**
   * Allowing override the option comparison algorithm for tracking identities when
   * checking for change
   */
  compareWithFn: (a: any, b: any) => boolean;
}

/**
 * Component that displays a dropdown with a single select option.
 *
 * @remarks
 * This component provides two ways to add dropdown items: via the `options` input property
 * for string or object arrays, or via content projection using {@link DropdownSingleSelectOptionComponent}.
 * When both methods are used simultaneously, the items are combined and displayed together.
 *
 * The component supports accessibility features through ARIA attributes and extends
 * {@link InputBaseComponent} for form integration capabilities.
 *
 * ### Accessibility
 * Current component provides option to pass needed accessibility
 * attributes. You need to take care of properties that are required in your case :
 *  - aria-label will be linked to the single select dropdown with label
 *  - set aria-labelledby with ID of another element in the DOM as dropdown's label.
 *  - set aria-describedby with ID of another element in the DOM with descriptive text about the dropdown
 *  - set aria-expanded with a boolean value based on the listbox display.
 *  - set aria-invalid to true when a selected option is not valid ans vice versa
 *  - aria-owns handles contextual relationship between a parent and its child elements,
 *    in this case between dropdown menu and dropdown items, use the id from the input binding.
 *
 * @example
 *
 * ```html
 * <!-- Via options array -->
 * <bb-dropdown-single-select-ui
 *   [placeholder]="'Select an option'"
 *   [formControl]="formControl"
 *   [options]="['Option One', 'Option Two', 'Option Three']"
 * />
 *
 * <!-- Via content children -->
 * <bb-dropdown-single-select-ui
 *   [placeholder]="'Select an option'"
 *   [formControl]="formControl"
 * >
 *   <bb-dropdown-single-select-option-ui label="Option One" value="1" />
 *   <bb-dropdown-single-select-option-ui label="Option Two" value="2" />
 * </bb-dropdown-single-select-ui>
 * ```
 */
@Component({
  selector: 'bb-dropdown-single-select-ui',
  templateUrl: './dropdown-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownSingleSelectComponent),
      multi: true,
    },
    {
      provide: BB_DROPDOWN_CHANGE_DETECTION_REF_TOKEN,
      useExisting: ChangeDetectorRef,
    },
  ],
  standalone: false,
})
export class DropdownSingleSelectComponent extends InputBaseComponent implements DropdownSingleSelectComponentProps {
  /**
   * The placeholder text displayed when no option is selected.
   *
   * @example
   *
   * ```html
   * <bb-dropdown-single-select-ui
   *   [placeholder]="'Select an option'"
   *   [formControl]="formControl"
   * />
   * ```
   */
  @Input({ transform: withDefaultString }) placeholder = '';

  /**
   * Whether to display the placeholder as the first option in the dropdown.
   *
   * @example
   *
   * ```html
   * <bb-dropdown-single-select-ui
   *   [defaultOptionAsPlaceholder]="true"
   *   placeholder="Select an option"
   *   [formControl]="formControl"
   * />
   * ```
   */
  @Input({ transform: withDefaultFalse }) defaultOptionAsPlaceholder = false;

  /**
   * Dropdown option items to display.
   *
   * @remarks
   * Accepts an array of option objects with `label` and `value` properties,
   * or an array of strings that serve as both label and value.
   *
   * @example
   *
   * ```html
   * <bb-dropdown-single-select-ui
   *   [options]="[{ label: 'Label', value: 'value' }]"
   * />
   * ```
   */
  @Input({ transform: withDefaultOptions }) options: Array<Object | string> = [];

  /**
   * The child option components of type {@link DropdownSingleSelectOptionComponent}.
   *
   * @example
   *
   * ```html
   * <bb-dropdown-single-select-ui>
   *   <bb-dropdown-single-select-option-ui label="Option One" value="1" />
   *   <bb-dropdown-single-select-option-ui label="Option Two" value="2" />
   * </bb-dropdown-single-select-ui>
   * ```
   */
  @ContentChildren(DropdownSingleSelectOptionComponent)
  contentOptions: QueryList<DropdownSingleSelectOptionComponent> | undefined;

  /**
   * Path to the object property used for displaying option text when options are objects.
   *
   * @example
   *
   * ```html
   * <bb-dropdown-single-select-ui
   *   [options]="objectOptions"
   *   displayAttributePath="name"
   * />
   * ```
   */
  @Input() displayAttributePath?: string;

  /**
   * Right corner icon name. Default name 'toggle-down'.
   *
   * @example
   *
   * ```html
   * <bb-dropdown-single-select-ui
   *   [iconName]="props.iconName"
   * />
   * ```
   */
  @Input({ transform: withDefaultIconName }) iconName = 'toggle-down';

  /**
   * Comparison function for tracking option identities when checking for changes.
   *
   * @example
   *
   * ```html
   * <bb-dropdown-single-select-ui
   *   [compareWithFn]="props.compareWithFn"
   * />
   * ```
   */
  @Input({ transform: withDefaultCompareWithFn }) compareWithFn: (a: any, b: any) => boolean = (a, b) => a === b;

  constructor(protected readonly cd: ChangeDetectorRef) {
    super(cd);
  }

  /**
   * Gets the display text for an option, resolving object paths if needed.
   *
   * @remarks
   * When options are objects and displayAttributePath is provided, this method
   * resolves the nested property path to extract the display text.
   *
   * @param option - The option item (string or object)
   * @returns The text to display for the option
   *
   * @example
   *
   * ```html
   * @for (option of options; track $index) {
   *   <option [ngValue]="option">
   *     {{ getOptionDisplayText(option) }}
   *   </option>
   * }
   * ```
   */
  getOptionDisplayText(option: Object | string) {
    return this.displayAttributePath && typeof option === 'object' && option !== null
      ? this.resolvePath(this.displayAttributePath, option as Option)
      : option;
  }

  private resolvePath(path: string, object: Option): string {
    if (!object && !path) {
      return '';
    }
    let paths;
    let propName: string;
    if (path.match(/^\[\d\]/) !== null) {
      paths = path.replace(/^[\[\]]/g, '').split(/\./);
      propName = paths[0].replace(/\]/, '')[0];
    } else {
      paths = path.split(/[\.\[]/);
      propName = paths[0];
    }

    const remainingPath = paths
      .slice(1)
      .reduce((result: string[], item: string) => {
        if (item) {
          if (item.match(/^\d\]/)) {
            item = '[' + item;
          }
          result.push(item);
        }

        return result;
      }, [])
      .join('.');

    if (!remainingPath) {
      const propValue = object[propName];

      return typeof propValue === 'string' ? propValue : propValue ? String(propValue) : '';
    } else if (object.hasOwnProperty(propName)) {
      return this.resolvePath(remainingPath, object[propName]);
    } else {
      return '';
    }
  }

  /**
   * @internal
   */
  protected onKeyDown(event: KeyboardEvent) {
    if (this.readonly) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
}
